import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { ClipboardList, ChevronDown, MapPin, Check, ArrowUpRight } from 'lucide-react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { TRADER_COLORS, getWikiLink } from '../../utils/constants';
import { TRADER_FALLBACK_IMAGES } from '../../utils/constants';
function ActiveQuestsList({ quests, allMaps, validTraders, sortBy, setSortBy, expandedQuest, setExpandedQuest, profile, onUpdate }) {
    const { t, language } = useLanguage();

    const handleQuestComplete = (questId) => {
        const updatedProfile = { ...profile, completedQuests: [...(profile.completedQuests || []), questId] };
        localStorage.setItem('tarkovProfile', JSON.stringify(updatedProfile));
        onUpdate(updatedProfile);
    };

    const handleObjectiveToggle = (questId, objectiveId) => {
        const questObjectives = (profile.completedObjectives || {})[questId] || [];
        const newQuestObjectives = questObjectives.includes(objectiveId)
            ? questObjectives.filter(id => id !== objectiveId)
            : [...questObjectives, objectiveId];
        const updatedProfile = { ...profile, completedObjectives: { ...(profile.completedObjectives || {}), [questId]: newQuestObjectives } };
        localStorage.setItem('tarkovProfile', JSON.stringify(updatedProfile));
        onUpdate(updatedProfile);
    };

    return (
<div className="lg:col-span-2 bg-[#09090b] border border-white/5 rounded-sm flex flex-col h-full overflow-hidden shadow-2xl relative">            {/* Header & Filters */}
            <div className="p-6 border-b border-white/5 flex items-center justify-between bg-[#0c0c0e]">
                <div className="flex items-center gap-3">
                    <ClipboardList className="w-5 h-5 text-emerald-500" strokeWidth={2} />
                    <h2 className="text-sm font-bold text-white uppercase tracking-widest">{t('active_quests')}</h2>
                </div>
                <div className="flex gap-2">
                    <select value={sortBy.startsWith('trader:') ? sortBy : ''} onChange={(e) => setSortBy(e.target.value || 'none')} className="bg-black border border-white/10 text-[10px] font-bold uppercase text-zinc-400 rounded-sm px-3 py-1.5 focus:outline-none focus:border-emerald-500 transition-colors">
                        <option value="">{t('all_traders')}</option>
                        {validTraders.map(trader => (
                            <option key={trader.id} value={`trader:${trader.name}`}>
                                {t(`trader_${trader.normalizedName || trader.name.toLowerCase()}`)}
                            </option>
                        ))}
                    </select>
                    <select value={sortBy.startsWith('map:') ? sortBy : ''} onChange={(e) => setSortBy(e.target.value || 'none')} className="bg-black border border-white/10 text-[10px] font-bold uppercase text-zinc-400 rounded-sm px-3 py-1.5 focus:outline-none focus:border-emerald-500 transition-colors">
                        <option value="">{t('all')} Maps</option>
                        {allMaps.map(m => <option key={m} value={`map:${m}`}>{m}</option>)}
                    </select>
                </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar bg-[#050505]">
                {quests.length > 0 ? quests.map((quest) => {
                    const isExpanded = expandedQuest === quest.id;
                    const questObjectives = (profile.completedObjectives || {})[quest.id] || [];
                    const requiredObjectives = quest.objectives.filter(obj => !obj.optional);
                    const completedRequired = requiredObjectives.filter(obj => questObjectives.includes(obj.id)).length;

                    const rawTraderName = quest.trader.normalizedName || quest.trader.name.toLowerCase();
                    const traderColor = TRADER_COLORS[rawTraderName] || TRADER_COLORS['default'];

                    return (
                        <div
                            key={quest.id}
                            className="group border rounded-sm transition-all duration-200 relative overflow-hidden"
                            style={{
                                backgroundColor: isExpanded ? '#0f1012' : `${traderColor}08`,
                                borderColor: isExpanded ? `${traderColor}40` : 'rgba(255,255,255,0.05)'
                            }}
                        >
                            {/* Sol Kenar Çizgisi (Senin Eklediğin Harika Detay) */}
                            <div
                                className="absolute left-0 top-0 bottom-0 w-1 transition-colors opacity-80 group-hover:opacity-100"
                                style={{ backgroundColor: traderColor }}
                            ></div>

                            <div className="p-3.5 pl-5 flex items-center gap-4 cursor-pointer" onClick={() => setExpandedQuest(isExpanded ? null : quest.id)}>
                                <div className="relative flex-shrink-0">
                                    <LazyLoadImage
                                        src={quest.trader?.imageLink || TRADER_FALLBACK_IMAGES[quest.trader?.normalizedName] || TRADER_FALLBACK_IMAGES['default']}
                                        alt="Trader"
                                        effect="blur"
                                        className="w-10 h-10 rounded-sm object-cover ring-1 ring-zinc-800 grayscale group-hover:grayscale-0 transition-all" />
                                    <div className="absolute -bottom-1 -right-1 bg-black border border-zinc-800 rounded px-1 text-[8px] font-mono font-bold text-zinc-400">
                                        {quest.minPlayerLevel}
                                    </div>
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5">
                                        <span className="text-sm font-bold truncate transition-colors text-zinc-200 group-hover:text-white">
                                            {quest.name}
                                        </span>
                                        {quest.kappaRequired && <span className="bg-yellow-500/10 text-yellow-500 border border-yellow-500/20 text-[9px] px-1.5 py-px rounded-sm font-mono font-bold uppercase tracking-wider">KAPPA</span>}
                                    </div>
                                    <div className="flex items-center gap-4 text-xs text-zinc-500 font-mono">
                                        <div className="flex items-center gap-1.5">
                                            <MapPin className="w-3 h-3 text-zinc-600 group-hover:text-zinc-400" />
                                            <span className="uppercase tracking-wide">{quest.mapName === 'Multiple' ? t('multiple') : quest.mapName}</span>
                                        </div>
                                        <div className="w-px h-3 bg-zinc-800"></div>
                                        <span className={completedRequired === requiredObjectives.length ? 'text-emerald-500 font-bold' : 'text-zinc-400'}>
                                            {`${completedRequired} / ${requiredObjectives.length}`}
                                        </span>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 mr-2">
                                    <a href={getWikiLink(quest, language)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="flex items-center gap-1.5 text-[10px] font-bold uppercase text-zinc-500 hover:text-blue-400 px-2 py-1.5 border border-transparent hover:border-blue-500/20 transition-colors">
                                        WIKI <ArrowUpRight className="w-3 h-3" />
                                    </a>
                                    <div className="w-px h-5 bg-zinc-800"></div>
                                    <button onClick={(e) => { e.stopPropagation(); handleQuestComplete(quest.id); }} className="w-8 h-8 rounded-sm bg-zinc-900/50 border border-zinc-700 text-zinc-500 hover:bg-emerald-600 hover:text-white flex items-center justify-center transition-all">
                                        <Check className="w-4 h-4" strokeWidth={3} />
                                    </button>
                                    <ChevronDown className={`w-4 h-4 text-zinc-600 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} style={{ color: isExpanded ? traderColor : undefined }} />
                                </div>
                            </div>

                            {/* Genişletilmiş Alan */}
                            {isExpanded && quest.objectives.length > 0 && (
                                <div className="border-t border-white/5 bg-black/30 p-3 pl-14 space-y-1">
                                    {quest.objectives.map(obj => {
                                        const isObjCompleted = questObjectives.includes(obj.id);
                                        return (
                                            <div key={obj.id} className="flex items-start gap-3 p-2 rounded hover:bg-white/5 cursor-pointer group/obj" onClick={() => handleObjectiveToggle(quest.id, obj.id)}>
                                                <div className={`mt-0.5 w-3.5 h-3.5 rounded-sm border flex items-center justify-center flex-shrink-0 transition-colors ${isObjCompleted ? 'bg-emerald-600 border-emerald-600' : 'border-zinc-600 group-hover/obj:border-emerald-500'}`}>
                                                    {isObjCompleted && <Check className="w-3 h-3 text-white" />}
                                                </div>
                                                <span className={`text-xs font-mono leading-relaxed ${isObjCompleted ? 'text-zinc-600 line-through' : 'text-zinc-300 group-hover/obj:text-white'}`}>
                                                    {obj.description}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                }) : (
                    <div className="flex flex-col items-center justify-center h-full text-zinc-600">
                        <ClipboardList className="w-16 h-16 opacity-10 mb-4" />
                        <p className="font-mono text-sm uppercase tracking-widest opacity-50">{t('no_active_quests')}</p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ActiveQuestsList;